'use client';

/**
 * «Выбор замены» (pipeline v2, node 'pick'): the source is parsed in the
 * wizard BEFORE the order (S1 through the engine), the parent sees its cast
 * and picks whom each of their heroes replaces. The order carries the parse
 * and the map {entityId → characterId} — never names.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2, ScanSearch } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/utils/cn';
import { preParseSource } from '@/contexts/pipeline-v2/actions/preParseSource';
import type { ParsedCastMember } from '@/contexts/pipeline-v2/story/preParse';

export interface ReplaceHero { characterId: string; name: string }
export type ReplaceMap = Array<{ entityId: string; characterId: string }>;

export interface ReplacePickerProps {
  /** a pasted text, or a basis (classic / public story) id */
  text?: string;
  basisStoryId?: string;
  language: string;
  ageGroup?: string;
  title?: string;
  /** the heroes the parent picked for this order */
  heroes: ReplaceHero[];
  value: ReplaceMap;
  /** parsed = null for a basis (its parse is stored server-side) */
  onChange: (next: { parsed: unknown | null; map: ReplaceMap }) => void;
}

export function ReplacePicker({ text, basisStoryId, language, ageGroup, title, heroes, value, onChange }: ReplacePickerProps) {
  const t = useTranslations('content.wizard.replace');
  const [cast, setCast] = useState<ParsedCastMember[] | null>(null);
  const [parsed, setParsed] = useState<unknown | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // a new source invalidates the parse and the picks
  const sourceKey = `${basisStoryId ?? ''}|${language}|${(text ?? '').length}:${(text ?? '').slice(0, 64)}`;
  const lastKey = useRef(sourceKey);
  useEffect(() => {
    if (lastKey.current === sourceKey) return;
    lastKey.current = sourceKey;
    setCast(null);
    setParsed(null);
    if (value.length) onChange({ parsed: null, map: [] });
  }, [sourceKey, value.length, onChange]);

  const canParse = !!basisStoryId || (text?.trim().length ?? 0) >= 100;

  const parse = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await preParseSource({ text, basisStoryId, language, ageGroup, title });
      if (!res.success) { setError(res.error); return; }
      setCast(res.data.cast);
      setParsed(res.data.parsed);
      onChange({ parsed: res.data.parsed, map: [] });
    } catch {
      setError(t('error'));
    } finally {
      setBusy(false);
    }
  };

  const pick = (entityId: string, characterId: string) => {
    // one hero replaces one character: picking a hero elsewhere moves it
    const rest = value.filter((m) => m.entityId !== entityId && m.characterId !== characterId);
    onChange({ parsed, map: characterId ? [...rest, { entityId, characterId }] : rest });
  };

  return (
    <div className="space-y-2 rounded-md border p-2" data-testid="replace-picker">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs sm:text-sm">{t('title')}</Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          disabled={!canParse || busy || !heroes.length}
          onClick={() => void parse()}
          data-testid="replace-picker-parse"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <ScanSearch className="size-3.5" />}
          {cast ? t('reparse') : t('parse')}
        </Button>
      </div>
      {!heroes.length && <p className="text-[11px] text-muted-foreground">{t('pickHeroesFirst')}</p>}
      {busy && <p className="text-[11px] text-muted-foreground" data-testid="replace-picker-busy">{t('busy')}</p>}
      {error && <p className="text-[11px] text-destructive" data-testid="replace-picker-error">{error}</p>}
      {cast && cast.length === 0 && <p className="text-[11px] text-muted-foreground">{t('noCast')}</p>}
      {cast && cast.length > 0 && (
        <ul className="space-y-1.5" data-testid="replace-picker-cast">
          {cast.map((c) => {
            const chosen = value.find((m) => m.entityId === c.entityId)?.characterId ?? '';
            return (
              <li key={c.entityId} className="flex flex-wrap items-center gap-1.5" data-testid={`replace-cast-${c.entityId}`}>
                <span className="min-w-0 truncate text-xs font-medium">{c.name}</span>
                <span className="text-[10px] text-muted-foreground">→</span>
                <button
                  type="button"
                  onClick={() => pick(c.entityId, '')}
                  className={cn('rounded-full border px-2 py-0.5 text-[10px]', !chosen ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent')}
                  data-testid={`replace-keep-${c.entityId}`}
                >
                  {t('keep')}
                </button>
                {heroes.map((h) => (
                  <button
                    key={h.characterId}
                    type="button"
                    onClick={() => pick(c.entityId, h.characterId)}
                    className={cn('rounded-full border px-2 py-0.5 text-[10px]', chosen === h.characterId ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent')}
                    aria-pressed={chosen === h.characterId}
                    data-testid={`replace-pick-${c.entityId}-${h.characterId}`}
                  >
                    {h.name}
                  </button>
                ))}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
